import { Component, DestroyRef, PLATFORM_ID, TransferState, inject, makeStateKey, signal } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { catchError, distinctUntilChanged, map, of, switchMap, tap } from 'rxjs';

import { FrontendSlugResolution } from '../../core/api/api.models';
import { SavingArenaApiService } from '../../core/api/saving-arena-api.service';
import { SsrResponseService } from '../../core/seo/ssr-response.service';
import { SlugPrefetchStore } from '../../core/api/slug-prefetch.store';
import { DealsProductPage } from '../deals/deals-product-page/deals-product-page';
import { NotFoundPage } from '../not-found/not-found-page/not-found-page';
import { StorePage } from '../store/store-page/store-page';

/**
 * The page behind a bare one-segment URL.
 *
 * `/zulily-coupon-codes` is a store and `/1-oz-silver-maples-monster-box-500-pc` is a product, and
 * the two URLs are the same shape. Angular cannot decide between them in the router: a matcher is
 * synchronous, and neither namespace is small enough to ship to the browser as a registry the way
 * blog slugs are — there are ~38,000 stores and the product catalogue grows with every feed import.
 *
 * So the server decides. `GET /api/frontend/resolve/:slug` answers with the type AND the whole
 * payload for that page, which is then handed to the page through `SlugPrefetchStore` — so a page
 * reached here costs exactly one request, the same as reaching it at its own URL, and `StorePage`
 * and `DealsProductPage` need no changes to work in both places.
 *
 * Stores are resolved first server-side, so a newly imported product that happens to slugify like an
 * existing store can never take that store's URL.
 */
@Component({
  selector: 'app-root-slug-page',
  imports: [StorePage, DealsProductPage, NotFoundPage],
  template: `
    @switch (kind()) {
      @case ('store') {
        <app-store-page />
      }
      @case ('product') {
        <app-deals-product-page />
      }
      @case ('missing') {
        <app-not-found-page />
      }
    }
  `,
})
export class RootSlugPage {
  private readonly route = inject(ActivatedRoute);
  private readonly api = inject(SavingArenaApiService);
  private readonly prefetch = inject(SlugPrefetchStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ssrResponse = inject(SsrResponseService);
  private readonly transferState = inject(TransferState);
  private readonly isServer = isPlatformServer(inject(PLATFORM_ID));

  /**
   * Nothing is rendered while this is empty. Committing to a page before the server has answered
   * would mount StorePage for a product URL, which fetches a store that does not exist and reports
   * a 404 for a page that is really there.
   */
  protected readonly kind = signal<'' | 'store' | 'product' | 'missing'>('');

  constructor() {
    this.route.paramMap
      .pipe(
        map((params) => (params.get('storeSlug') ?? '').trim()),
        // `/<store>/coupon/<id>` is the same route with one more param, so opening and closing the
        // coupon modal emits here without the slug changing. Re-resolving on that would clear
        // `kind`, unmount StorePage and refetch the whole store — the exact destroy-and-recreate
        // this route is shaped to avoid. Only a genuinely different slug is a new page.
        distinctUntilChanged(),
        switchMap((slug) => {
          this.kind.set('');
          this.prefetch.clear();

          if (!slug) return of({ slug: '', resolution: null });

          // This resolver runs again on the client during hydration (same component, same
          // constructor) with no built-in HTTP transfer-state cache in this app (see deals-page.ts
          // for the one place that hand-rolls it) — so without this, every one of the ~42,000
          // store/product pages re-fetched data the server had already resolved, and a renderer
          // that executes JS (Googlebot's included) ended up making that second request itself,
          // which is why `/api/frontend/resolve/<slug>` shows up as its own crawled URL.
          const stateKey = makeStateKey<FrontendSlugResolution | null>(`slug-resolution:${slug}`);
          if (this.transferState.hasKey(stateKey)) {
            return of({ slug, resolution: this.transferState.get(stateKey, null) });
          }

          return this.api.resolveSlug(slug).pipe(
            map((response) => (response.success ? response.data : null)),
            tap((resolution) => {
              if (this.isServer) this.transferState.set(stateKey, resolution);
            }),
            map((resolution) => ({ slug, resolution })),
            catchError((error: unknown) => {
              // This resolver gates every one-segment URL on the site — ~42,000 store and product
              // pages. A failure here never mounts StorePage at all, it mounts NotFoundPage, so
              // the cause has to be recorded HERE or a slow API retires the URL with a 404.
              const transient = this.ssrResponse.noteFetchFailure(error);
              // Only a confirmed 404 is worth caching — caching a transient failure (API briefly
              // down) would have the client's hydration replay believe a real page is missing
              // instead of retrying, same trap the 404-vs-503 distinction above exists to avoid.
              if (this.isServer && !transient) this.transferState.set(stateKey, null);
              return of({ slug, resolution: null });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ slug, resolution }) => {
        if (!resolution) {
          this.kind.set('missing');
          return;
        }

        // The payload is handed over BEFORE the page mounts, so the page's own fetch finds it
        // already there rather than going back to the network for what we just received.
        if (resolution.type === 'store' && resolution.store) {
          this.prefetch.setStore(slug, resolution.store);
          this.kind.set('store');
          return;
        }

        if (resolution.type === 'product' && resolution.product) {
          this.prefetch.setProduct(slug, resolution.product);
          this.kind.set('product');
          return;
        }

        this.kind.set('missing');
      });
  }
}
